import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { adminMutate, apiGet, useToast } from '../admin/primitives.js'
import { Field, NumberInput, SwitchRow } from '../admin/form.js'

/**
 * RECHERCHE WEB — CE QUE ÇA RAPPORTE, PAS CE QUE ÇA FAIT.
 *
 * La page annonçait « Recherche web » sans jamais dire ce que la branche
 * produisait. Derrière, la vérité était : 68 requêtes enregistrées, 4 avec une
 * correspondance, 1 résultat cumulé, 0 événement — et aucun moteur configuré.
 * L'écran affichait une source ; il n'y en avait pas.
 *
 * Il montre désormais l'ENTONNOIR complet : recherches, pages trouvées, pages
 * réellement lues, signaux, soirées. Une source qui rend mille résultats et
 * zéro signal ne sert à rien, et cela doit se voir d'un coup d'œil.
 *
 * Le nom du composant technique reste au diagnostic : l'utilisateur métier lit
 * « Recherche web », pas le nom du moteur.
 */

interface Sante {
  disponible: boolean
  composant: string | null
  version: string | null
  moteurs_actifs: number | null
  latence_ms: number | null
  message: string
}

interface Query {
  id: string; venue_id: string; venue_name: string
  label: string | null; query: string
  query_type: 'AUTO' | 'CUSTOM'; enabled: boolean
  last_run_at: string | null; last_success_at: string | null; last_error: string | null
  result_count: number; useful_content_count: number
  signal_count: number; event_count: number
}

interface Vue {
  sante: Sante
  config: { enabled: boolean; base_url: string | null; max_results: number; max_fetch: number }
  rendement: {
    requetes_actives: number; requetes_total: number
    urls_connues: number; urls_30j: number; pages_lues: number
    signaux_30j: number; events_30j: number; events_enrichis_30j: number
    derniere_collecte: string | null
  }
  queries: Query[]
}

function quandFr(iso: string | null): string {
  if (!iso) return 'jamais'
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Paris',
  })
}

export function WebSearchPanel({ peutGerer }: { peutGerer: boolean }) {
  const toast = useToast()
  const [v, setV] = useState<Vue | null>(null)
  const [url, setUrl] = useState('')
  const [actif, setActif] = useState(false)
  const [max, setMax] = useState(10)
  const [maxFetch, setMaxFetch] = useState(3)
  const [test, setTest] = useState(false)
  const [nouvelle, setNouvelle] = useState<{ venue: string; q: string }>({ venue: '', q: '' })

  const charger = (probe: boolean) => {
    void apiGet<Vue>(`/api/v1/soirees/websearch${probe ? '' : '?probe=0'}`).then((d) => {
      setV(d)
      setUrl(d.config.base_url ?? '')
      setActif(d.config.enabled)
      setMax(d.config.max_results)
      setMaxFetch(d.config.max_fetch)
    }).catch(() => setV(null))
  }
  // Pas de sonde au simple affichage : consulter une page ne doit pas déclencher
  // une recherche réelle chez les moteurs amont.
  useEffect(() => charger(false), [])

  const enregistrer = async () => {
    await adminMutate('PUT', '/api/v1/soirees/settings', {
      websearch_enabled: actif, websearch_base_url: url.trim(),
      websearch_max_results: max, websearch_max_fetch: maxFetch,
    })
  }

  const verifier = async () => {
    setTest(true)
    await enregistrer()
    await adminMutate('POST', '/api/v1/soirees/settings/websearch/check')
    setTest(false)
    charger(true)
  }

  const basculer = async (q: Query) => {
    await adminMutate('PATCH', `/api/v1/soirees/websearch/queries/${q.id}`, { enabled: !q.enabled })
    charger(false)
  }

  const supprimer = async (q: Query) => {
    const r = await adminMutate<{ error?: string; message?: string }>(
      'DELETE', `/api/v1/soirees/websearch/queries/${q.id}`)
    if (r.status === 200) {
      toast('ok', 'Recherche supprimée. Ce qu’elle a déjà trouvé est conservé.')
      charger(false)
    } else toast('error', r.data?.message ?? 'Suppression impossible.')
  }

  const ajouter = async () => {
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', '/api/v1/soirees/websearch/queries',
      { venue_id: nouvelle.venue, query: nouvelle.q.trim() })
    if (r.status === 201) { setNouvelle({ venue: nouvelle.venue, q: '' }); charger(false) }
    else toast('error', r.data?.message ?? 'Ajout impossible.')
  }

  if (!v) return null

  const etablissements = [...new Map(v.queries.map((q) => [q.venue_id, q.venue_name])).entries()]
  const actives = v.queries.filter((q) => q.enabled)
  const dormantes = v.queries.filter((q) => !q.enabled)

  return (
    <>
      <section className="lc-panel gd-panel" aria-labelledby="set-websearch">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="set-websearch">Recherche web</h2>
        </header>
        <div className="lc-panel__body">
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Le moteur tourne sur nos serveurs : aucun abonnement, aucune clé, aucun
            appel facturé. Tant qu’aucune instance n’est renseignée, ce canal reste
            éteint — et les soirées qu’il aurait pu révéler restent invisibles.
          </p>

          <dl className="lc-kv lc-kv--blocs" style={{ marginTop: 14 }}>
            <div>
              <dt>État</dt>
              <dd>
                <span className={`lc-dot ${v.sante.disponible ? 'lc-dot--ok' : 'lc-dot--warning'}`}
                  aria-hidden="true" />{' '}
                {v.sante.disponible ? 'Opérationnel' : 'Indisponible'}
              </dd>
            </div>
            <div><dt>Moteur</dt><dd>Auto-hébergé</dd></div>
            <div><dt>Dernière collecte</dt><dd>{quandFr(v.rendement.derniere_collecte)}</dd></div>
            <div><dt>Recherches actives</dt>
              <dd>{v.rendement.requetes_actives} sur {v.rendement.requetes_total}</dd></div>
          </dl>

          {/* L'entonnoir. Ce n'est pas « beaucoup de résultats » qui compte, mais
              ce qui en ressort d'utile. */}
          <h3 className="so-class-title" style={{ marginTop: 18 }}>Rendement sur 30 jours</h3>
          <dl className="lc-kv lc-kv--blocs">
            <div><dt>Pages trouvées</dt><dd>{v.rendement.urls_30j}</dd></div>
            <div><dt>Pages réellement lues</dt><dd>{v.rendement.pages_lues}</dd></div>
            <div><dt>Signaux produits</dt><dd>{v.rendement.signaux_30j}</dd></div>
            {/*
              DÉCOUVERTES ET ENRICHIES sont deux choses, et les additionner
              flatterait la source. Une soirée « enrichie » existait déjà —
              repérée par les Stories, par exemple — et la recherche web n'a
              fait que lui ajouter un signal.
            */}
            <div><dt>Soirées découvertes</dt><dd>{v.rendement.events_30j}</dd></div>
            <div><dt>Soirées enrichies</dt><dd>{v.rendement.events_enrichis_30j}</dd></div>
          </dl>
          {v.rendement.signaux_30j === 0 && v.rendement.urls_30j > 0 ? (
            <p className="lc-hint" style={{ marginTop: 8 }}>
              Des pages sont trouvées mais aucune n’a encore livré de signal
              exploitable. C’est une information, pas une panne : cette source se
              juge sur la durée.
            </p>
          ) : null}

          <SwitchRow
            checked={actif} onChange={setActif} disabled={!peutGerer || url.trim() === ''}
            title="Recherche web activée"
            desc="Complète la veille avec les résultats d’un métamoteur auto-hébergé."
          />

          <div className="rb-cond" style={{ marginTop: 12 }}>
            <Field label="Adresse de l’instance"
              helper="Interne au serveur. Ce n’est pas un secret : aucune clé n’est nécessaire.">
              <input className="lc-input" type="text" value={url} disabled={!peutGerer}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="http://searxng:8080" aria-label="Adresse de l’instance de recherche" />
            </Field>
            <Field label="Résultats retenus" helper="Au-delà, le bruit dépasse l’apport.">
              <NumberInput value={max} onChange={(x) => setMax(Number(x) || 10)} min={1} max={50}
                ariaLabel="Nombre de résultats retenus" />
            </Field>
            <Field label="Pages lues par recherche"
              helper="Seules les pages NOUVELLES sont téléchargées ; au-delà, on se contente de l’extrait.">
              <NumberInput value={maxFetch} onChange={(x) => setMaxFetch(Number(x) || 3)} min={0} max={10}
                ariaLabel="Pages téléchargées par recherche" />
            </Field>
          </div>

          <p className="lc-hint" style={{ marginTop: 10 }}>{v.sante.message}</p>

          {/* Le composant technique appartient au diagnostic, pas à l'écran métier. */}
          <details style={{ marginTop: 10 }}>
            <summary className="lc-hint" style={{ cursor: 'pointer' }}>Diagnostic</summary>
            <dl className="lc-kv lc-kv--blocs" style={{ marginTop: 8 }}>
              <div><dt>Composant</dt><dd>{v.sante.composant ?? '—'}</dd></div>
              <div><dt>Version</dt><dd>{v.sante.version ?? '—'}</dd></div>
              <div><dt>Moteurs actifs</dt><dd>{v.sante.moteurs_actifs ?? '—'}</dd></div>
              <div><dt>Latence</dt>
                <dd>{v.sante.latence_ms !== null ? `${v.sante.latence_ms} ms` : '—'}</dd></div>
            </dl>
          </details>

          {peutGerer ? (
            <button type="button" className="lc-btn" style={{ marginTop: 14 }}
              onClick={() => void verifier()} disabled={test || url.trim() === ''}>
              {test ? 'Vérification…' : 'Enregistrer et vérifier l’instance'}
            </button>
          ) : null}
        </div>
      </section>

      <section className="lc-panel gd-panel" aria-labelledby="set-ws-queries">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="set-ws-queries">Recherches</h2>
        </header>
        <div className="lc-panel__body">
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Quelques recherches de qualité valent mieux qu’une longue liste : les
            moteurs se ferment sous rafale, et une requête qui ne rapporte rien
            coûte quand même du temps à chaque tour.
          </p>

          <ul className="lc-list" style={{ marginTop: 12 }}>
            {actives.map((q) => (
              <li key={q.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <strong style={{ color: 'var(--text-1)' }}>{q.label ?? q.query}</strong>
                  <span className="lc-hint">{q.venue_name}</span>
                  {peutGerer ? (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button type="button" className="lc-btn lc-btn--ghost"
                        onClick={() => void basculer(q)}>Désactiver</button>
                      <button type="button" className="lc-iconbtn"
                        aria-label={`Supprimer la recherche ${q.query}`}
                        onClick={() => void supprimer(q)}>
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    </span>
                  ) : null}
                </div>
                <p className="lc-hint" style={{ margin: '4px 0 0' }}>
                  {q.result_count} résultat{q.result_count > 1 ? 's' : ''}
                  {' · '}{q.useful_content_count} page{q.useful_content_count > 1 ? 's' : ''} lue{q.useful_content_count > 1 ? 's' : ''}
                  {' · '}dernière recherche {quandFr(q.last_run_at)}
                  {q.last_error ? ` · ${q.last_error}` : ''}
                </p>
              </li>
            ))}
          </ul>

          {dormantes.length > 0 ? (
            <details style={{ marginTop: 12 }}>
              <summary className="lc-hint" style={{ cursor: 'pointer' }}>
                {dormantes.length} recherche{dormantes.length > 1 ? 's' : ''} en veille
              </summary>
              <ul className="lc-list" style={{ marginTop: 8 }}>
                {dormantes.map((q) => (
                  <li key={q.id} style={{
                    display: 'flex', gap: 10, flexWrap: 'wrap',
                    alignItems: 'baseline', padding: '6px 0',
                  }}>
                    <span style={{ color: 'var(--text-2)' }}>{q.query}</span>
                    <span className="lc-hint">{q.venue_name}</span>
                    {peutGerer ? (
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button type="button" className="lc-btn lc-btn--ghost"
                          onClick={() => void basculer(q)}>Réactiver</button>
                        <button type="button" className="lc-iconbtn"
                          aria-label={`Supprimer la recherche ${q.query}`}
                          onClick={() => void supprimer(q)}>
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {peutGerer ? (
            <div className="so-rooms__form" style={{ marginTop: 16 }}>
              <p className="so-rooms__form__title">Ajouter une recherche</p>
              <label className="so-rooms__field">
                <span className="lc-hint">Établissement</span>
                <select className="lc-input" value={nouvelle.venue} aria-label="Établissement"
                  onChange={(e) => setNouvelle({ ...nouvelle, venue: e.target.value })}>
                  <option value="">Choisir…</option>
                  {etablissements.map(([id, nom]) => <option key={id} value={id}>{nom}</option>)}
                </select>
              </label>
              <label className="so-rooms__field">
                <span className="lc-hint">Recherche</span>
                <input className="lc-input" value={nouvelle.q} maxLength={200}
                  placeholder="« Le Satori » Rennes programmation" aria-label="Recherche"
                  onChange={(e) => setNouvelle({ ...nouvelle, q: e.target.value })} />
              </label>
              <div className="so-rooms__form__actions">
                <button type="button" className="lc-btn lc-btn--primary"
                  disabled={!nouvelle.venue || nouvelle.q.trim().length < 2}
                  onClick={() => void ajouter()}>
                  <Plus size={14} aria-hidden="true" /> Ajouter
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  )
}
